import { useQuery } from "@tanstack/react-query";
import api, { inr } from "@/lib/api";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, BarChart, Bar, CartesianGrid } from "recharts";
import { Loader2, IndianRupee, ShoppingCart, Users, AlertTriangle, TrendingDown, Package } from "lucide-react";

const CARD_DEFS = [
  { key: "today_revenue", label: "Today's Revenue", money: true, icon: IndianRupee },
  { key: "today_orders", label: "Today's Orders", icon: ShoppingCart },
  { key: "total_revenue", label: "Total Revenue", money: true, icon: IndianRupee },
  { key: "total_orders", label: "Total Orders", icon: ShoppingCart },
  { key: "total_customers", label: "Customers", icon: Users },
  { key: "new_customers", label: "New Today", icon: Users },
  { key: "digital_revenue", label: "Digital Revenue", money: true, icon: IndianRupee },
  { key: "physical_revenue", label: "Physical Revenue", money: true, icon: Package },
  { key: "pending_orders", label: "Pending Orders", icon: ShoppingCart },
  { key: "failed_payments", label: "Failed Payments", icon: AlertTriangle },
  { key: "abandoned_carts", label: "Abandoned Carts", icon: TrendingDown },
  { key: "upcoming_interest", label: "Upcoming Interest", icon: Users },
];

export default function Dashboard() {
  const { data, isLoading } = useQuery({ queryKey: ["admin-dashboard"], queryFn: async () => (await api.get("/admin/dashboard")).data });
  if (isLoading) return <Loader2 className="w-8 h-8 animate-spin text-teal" />;
  const c = data.cards;
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-white">Dashboard</h1>
        <span className={`text-xs px-3 py-1 rounded-full border ${data.payment_mode.includes("LIVE") ? "bg-teal-500/10 text-teal-400 border-teal-500/30" : "bg-amber-500/10 text-amber-400 border-amber-500/30"}`}>
          Payments: {data.payment_mode}
        </span>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {CARD_DEFS.map((cd) => {
          const Icon = cd.icon;
          return (
            <div key={cd.key} className="bg-slate-900 border border-white/10 rounded-xl p-4" data-testid={`dash-card-${cd.key}`}>
              <div className="flex items-center justify-between"><span className="text-xs text-slate-400">{cd.label}</span><Icon className="w-4 h-4 text-teal-500" /></div>
              <div className="text-2xl font-bold text-white mt-2">{cd.money ? inr(c[cd.key]) : c[cd.key]}</div>
            </div>
          );
        })}
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-slate-900 border border-white/10 rounded-xl p-5">
          <h3 className="text-white font-semibold mb-4">Revenue — last 14 days</h3>
          <ResponsiveContainer width="100%" height={240}>
            <LineChart data={data.revenue_series}>
              <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
              <XAxis dataKey="date" tick={{ fill: "#64748b", fontSize: 11 }} />
              <YAxis tick={{ fill: "#64748b", fontSize: 11 }} />
              <Tooltip contentStyle={{ background: "#0f172a", border: "1px solid #1e293b", borderRadius: 8, color: "#fff" }} />
              <Line type="monotone" dataKey="revenue" stroke="#14B8A6" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
        <div className="bg-slate-900 border border-white/10 rounded-xl p-5">
          <h3 className="text-white font-semibold mb-4">Product performance</h3>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={data.product_performance}>
              <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
              <XAxis dataKey="name" tick={{ fill: "#64748b", fontSize: 9 }} interval={0} angle={-15} textAnchor="end" height={50} />
              <YAxis tick={{ fill: "#64748b", fontSize: 11 }} />
              <Tooltip contentStyle={{ background: "#0f172a", border: "1px solid #1e293b", borderRadius: 8, color: "#fff" }} />
              <Bar dataKey="revenue" fill="#0D9488" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="bg-slate-900 border border-white/10 rounded-xl p-5">
        <h3 className="text-white font-semibold mb-4">Conversion funnel</h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {[["Product Views", "product_view"], ["Add to Cart", "add_to_cart"], ["Checkout", "checkout_start"], ["Purchases", "purchase"]].map(([label, key]) => (
            <div key={key} className="text-center">
              <div className="text-3xl font-bold text-teal-400">{data.funnel[key] || 0}</div>
              <div className="text-xs text-slate-400 mt-1">{label}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
